import { sidebarNavigationGroups } from './sidebarNavigation.ts'

export const PAGE_HISTORY_LIMIT = 20
export const PAGE_HISTORY_STORAGE_KEY = 'apex:page-history'

export interface PageHistoryEntry {
  key: string
  href: string
  title: string
}

const routeTitles = new Map<string, string>([
  ['/', '首页'],
  ...sidebarNavigationGroups.flatMap(group =>
    group.children.map(item => [item.key, item.label] as [string, string]),
  ),
  ['/slash/cards', 'Slash 卡台'],
  ['/photonpay', '光子易卡台'],
  ['/photonpay/credentials', '光子易凭证'],
  ['/wasabi', 'WasabiCard 卡台'],
  ['/wasabi/credentials', 'WasabiCard 凭证'],
  ['/wasabi/wallet-transactions', 'Wasabi 钱包流水'],
  ['/extension/invite-assign', '邀请分配'],
  ['/extension/pixel-share', '像素共享'],
  ['/extension/batch-rename', '批量改名'],
])

const detailRouteTitles: Array<[RegExp, string]> = [
  [/^\/slash\/cards\/[^/]+$/, 'Slash 卡片详情'],
  [/^\/slash\/card-groups\/[^/]+$/, 'Slash 预算组详情'],
  [/^\/photonpay\/cards\/[^/]+$/, '光子易卡片详情'],
  [/^\/photonpay\/cardholders\/[^/]+$/, '光子易持卡人详情'],
  [/^\/photonpay\/cardholder-groups\/[^/]+$/, '光子易预算组详情'],
  [/^\/wasabi\/cards\/[^/]+$/, 'Wasabi 卡片详情'],
  [/^\/wasabi\/accounts\/[^/]+\/transactions$/, 'Wasabi 账户流水'],
]

export const HOME_PAGE_HISTORY_ENTRY: PageHistoryEntry = {
  key: '/',
  href: '/',
  title: '首页',
}

export function resolvePageHistoryTitle(pathname: string): string {
  const directTitle = routeTitles.get(pathname)
  if (directTitle) return directTitle

  const detailTitle = detailRouteTitles.find(([pattern]) => pattern.test(pathname))
  return detailTitle?.[1] || '页面'
}

export function createPageHistoryEntry(
  pathname: string,
  search = '',
  hash = '',
): PageHistoryEntry {
  return {
    key: pathname,
    href: `${pathname}${search}${hash}`,
    title: resolvePageHistoryTitle(pathname),
  }
}

export function addPageHistoryEntry(
  entries: readonly PageHistoryEntry[],
  entry: PageHistoryEntry,
  limit = PAGE_HISTORY_LIMIT,
): PageHistoryEntry[] {
  const previousIndex = entries.findIndex(item => item.key === entry.key)
  const earlierEntries = previousIndex < 0 ? entries : entries.slice(0, previousIndex)
  return [...earlierEntries, entry].slice(-limit)
}

export function readPageHistory(storage: Pick<Storage, 'getItem'>): PageHistoryEntry[] {
  try {
    const parsed = JSON.parse(storage.getItem(PAGE_HISTORY_STORAGE_KEY) || '[]')
    if (!Array.isArray(parsed)) return []

    return parsed
      .filter((item): item is PageHistoryEntry => (
        typeof item?.key === 'string'
        && item.key.startsWith('/')
        && typeof item?.href === 'string'
        && item.href.startsWith('/')
        && typeof item?.title === 'string'
      ))
      .slice(-PAGE_HISTORY_LIMIT)
  } catch {
    return []
  }
}

export function writePageHistory(
  storage: Pick<Storage, 'setItem'>,
  entries: readonly PageHistoryEntry[],
): void {
  try {
    storage.setItem(PAGE_HISTORY_STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // 浏览器禁用存储时，页签仍可在当前渲染周期内使用。
  }
}
